// Iter AV — session-aware security layer.
// Two separate controls: the auth token (JWT, unchanged) and an app-level
// lock + server-anchored "treatment window" that suspends the 10-minute
// inactivity logout only while an appointment is intentionally in progress.
import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, getToken } from "../lib/api";
import { unlockWithPasskey } from "../lib/webauthn";
import { useAuth } from "./AuthContext";

const IDLE_WARN_MS = 9 * 60 * 1000;
const IDLE_LOGOUT_MS = 10 * 60 * 1000;
const WINDOW_WARN_MS = 5 * 60 * 1000;

const SecurityContext = createContext(null);

export const SecurityProvider = ({ children }) => {
  const { user, loading: authLoading, logout } = useAuth();
  const [locked, setLockedState] = useState(() => sessionStorage.getItem("pstr_locked") === "1");
  const [idleWarning, setIdleWarning] = useState(false);
  const [windowInfo, setWindowInfo] = useState(null); // { sessionId, expiresAtMs }
  const [windowWarning, setWindowWarning] = useState(false);
  const lastActivity = useRef(Date.now());

  const setLocked = useCallback((v) => {
    sessionStorage.setItem("pstr_locked", v ? "1" : "0");
    setLockedState(v);
  }, []);

  // Reopened tab / new device: require re-auth before showing any PHI.
  useEffect(() => {
    if (getToken() && !sessionStorage.getItem("pstr_tab_alive")) setLocked(true);
    sessionStorage.setItem("pstr_tab_alive", "1");
  }, [setLocked]);

  const refreshWindow = useCallback(async () => {
    if (!getToken()) return;
    try {
      const r = await api.get("/security/window");
      if (r.data.active) {
        // Anchor to the server clock so a device-clock change can't extend it.
        const skew = Date.now() - new Date(r.data.server_now).getTime();
        setWindowInfo({ sessionId: r.data.session_id, expiresAtMs: new Date(r.data.expires_at).getTime() + skew });
      } else {
        setWindowInfo(null);
      }
    } catch { /* window state is best-effort; the tick keeps enforcing */ }
  }, []);

  useEffect(() => {
    if (user) { lastActivity.current = Date.now(); refreshWindow(); }
    else if (!authLoading) { setLocked(false); setIdleWarning(false); setWindowInfo(null); setWindowWarning(false); }
  }, [user, authLoading, refreshWindow, setLocked]);

  useEffect(() => {
    const mark = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  // Enforcement tick — activity extends the idle timer but NEVER the treatment window.
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      if (locked) return;
      const now = Date.now();
      if (windowInfo) {
        if (now >= windowInfo.expiresAtMs) {
          setWindowInfo(null);
          setWindowWarning(false);
          setLocked(true);
          api.post("/security/window/end").catch(() => {});
        } else {
          setIdleWarning(false);
          setWindowWarning(windowInfo.expiresAtMs - now <= WINDOW_WARN_MS);
        }
        return;
      }
      const idle = now - lastActivity.current;
      if (idle >= IDLE_LOGOUT_MS) {
        setIdleWarning(false);
        logout();
        toast("Signed out due to inactivity", { description: "For your security, your session ended after 10 minutes of inactivity." });
      } else {
        setIdleWarning(idle >= IDLE_WARN_MS);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [user, locked, windowInfo, logout, setLocked]);

  const lockNow = useCallback(() => {
    setIdleWarning(false);
    setWindowWarning(false);
    setLocked(true);
  }, [setLocked]);

  const unlock = useCallback(async (password) => {
    try {
      await api.post("/auth/verify-password", { password });
      lastActivity.current = Date.now();
      setLocked(false);
      refreshWindow();
      return { ok: true };
    } catch (e) {
      if (e.response?.status === 401 && e.response?.data?.detail !== "Incorrect password") {
        logout();
        return { ok: false, error: "Your session expired — please sign in again." };
      }
      return { ok: false, error: e.response?.data?.detail || "Incorrect password" };
    }
  }, [setLocked, refreshWindow, logout]);

  const unlockBiometric = useCallback(async () => {
    try {
      await unlockWithPasskey();
      lastActivity.current = Date.now();
      setLocked(false);
      refreshWindow();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || "Biometric unlock failed — use your password" };
    }
  }, [setLocked, refreshWindow]);

  const staySignedIn = useCallback(() => {
    lastActivity.current = Date.now();
    setIdleWarning(false);
  }, []);

  const startWindow = useCallback(async (sessionId, durationMinutes) => {
    if (!sessionId) return;
    try {
      await api.post("/security/window/start", { session_id: sessionId, duration_minutes: durationMinutes || 60 });
      await refreshWindow();
    } catch { /* non-blocking */ }
  }, [refreshWindow]);

  const extendWindow = useCallback(async () => {
    try {
      await api.post("/security/window/extend", { minutes: 15 });
      setWindowWarning(false);
      await refreshWindow();
      toast.success("Treatment window extended by 15 minutes");
    } catch {
      toast.error("Could not extend the window");
    }
  }, [refreshWindow]);

  const endWindow = useCallback(async () => {
    try { await api.post("/security/window/end"); } catch { /* best-effort */ }
    lastActivity.current = Date.now();
    setWindowInfo(null);
    setWindowWarning(false);
  }, []);

  const value = useMemo(() => ({
    locked, idleWarning, windowInfo, windowWarning,
    lockNow, unlock, unlockBiometric, staySignedIn, startWindow, extendWindow, endWindow, signOutNow: logout,
  }), [locked, idleWarning, windowInfo, windowWarning, lockNow, unlock, unlockBiometric, staySignedIn, startWindow, extendWindow, endWindow, logout]);

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
};

export const useSecurity = () => useContext(SecurityContext);
